import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import dagre from "@dagrejs/dagre";
import TraversalGraph from "./TraversalGraph";
import FloatingPanel from "../brain/FloatingPanel";
import { PANEL_STATES, RESIZE_DIRECTIONS } from "../brain/floatingPanelConstants";

/* ── Window shell classes (positioning, size and z-index come from FloatingPanel) ── */
const MAP_FRAME_CLASS_INLINE = "rounded-lg overflow-hidden flex flex-col bg-white shadow-2xl border border-cyan-200 rounded-xl";
const MAP_FRAME_CLASS_FULL = "h-screen w-screen overflow-hidden flex flex-col bg-white shadow-2xl rounded-none";
const MAP_FRAME_PROPS = { "data-diagmap-frame": true };
const MAP_RESIZE_CLASSES = {
  [RESIZE_DIRECTIONS.RIGHT]: "absolute right-0 top-14 bottom-0 w-1.5 cursor-ew-resize z-50 hover:bg-cyan-400/20 transition-colors",
  [RESIZE_DIRECTIONS.BOTTOM]: "absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize z-50 hover:bg-cyan-400/20 transition-colors",
  [RESIZE_DIRECTIONS.CORNER]: "absolute right-0 bottom-0 w-4 h-4 cursor-nwse-resize z-[51]",
};

/* ── Warm modern colors matching Mermaid Explorer (Image #15) ── */
const GNODE_STYLES = {
  Product:         { bg: "#64748b", border: "#475569", text: "#ffffff", icon: "P" },  // slate
  Problem:         { bg: "#ef4444", border: "#dc2626", text: "#ffffff", icon: "!" },  // red
  Symptom:         { bg: "#f97316", border: "#ea580c", text: "#ffffff", icon: "S" },  // orange
  Workflow:        { bg: "#6366f1", border: "#4f46e5", text: "#ffffff", icon: "W" },  // indigo
  Decision:        { bg: "#0ea5e9", border: "#0284c7", text: "#ffffff", icon: "?" },  // sky
  DiagnosticStep:  { bg: "#eab308", border: "#ca8a04", text: "#ffffff", icon: "D" },  // yellow
  DiagnosticSteps: { bg: "#eab308", border: "#ca8a04", text: "#ffffff", icon: "D" },  // yellow
  PossibleCause:   { bg: "#a855f7", border: "#9333ea", text: "#ffffff", icon: "C" },  // purple
  Resolution:      { bg: "#60a5fa", border: "#2563eb", text: "#ffffff", icon: "R" },  // blue
  Remediation:     { bg: "#14b8a6", border: "#0d9488", text: "#ffffff", icon: "Rx" }, // teal
  Workaround:      { bg: "#84cc16", border: "#65a30d", text: "#ffffff", icon: "W" },  // lime
  Info:            { bg: "#ec4899", border: "#db2777", text: "#ffffff", icon: "i" },  // pink
  _default:        { bg: "#6b7280", border: "#4b5563", text: "#ffffff", icon: "." },  // gray
};

const LEGEND_SHAPE = {
  Product:         (p) => <><path d={`M1,5 A6,3 0 0,1 13,5 L13,8 A6,3 0 0,1 1,8 Z`} {...p} /><ellipse cx="7" cy="5" rx="6" ry="3" {...p} /></>,
  Problem:         (p) => <polygon points="7,0 14,6 7,12 0,6" {...p} />,
  Symptom:         (p) => <polygon points="3,0 14,0 11,12 0,12" {...p} />,
  Workflow:        (p) => <><rect x="0" y="0" width="14" height="12" {...p} /><line x1="3" y1="0" x2="3" y2="12" stroke={p.stroke} strokeWidth="0.8" /><line x1="11" y1="0" x2="11" y2="12" stroke={p.stroke} strokeWidth="0.8" /></>,
  Decision:        (p) => <polygon points="7,0 14,6 7,12 0,6" {...p} />,
  DiagnosticStep:  (p) => <rect x="0" y="0" width="14" height="12" {...p} />,
  DiagnosticSteps: (p) => <rect x="0" y="0" width="14" height="12" {...p} />,
  PossibleCause:   (p) => <polygon points="0,6 3,0 11,0 14,6 11,12 3,12" {...p} />,
  Resolution:      (p) => <rect x="0" y="0" width="14" height="12" rx="6" ry="6" {...p} />,
  Remediation:     (p) => <rect x="0" y="0" width="14" height="12" rx="3" ry="3" {...p} />,
  Workaround:      (p) => <polygon points="3,0 11,0 14,12 0,12" {...p} />,
  Info:            (p) => <polygon points="0,0 14,0 14,12 0,12 3,6" {...p} />,
  _default:        (p) => <rect x="0" y="0" width="14" height="12" {...p} />,
};

// Clipboard with a legacy fallback: navigator.clipboard is undefined on
// non-secure origins (plain-http staging), and writeText rejects when the tab
// is not focused — the copy button must still work there.
const copyTextToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch (_) { /* fall through to the legacy path */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) { return false; }
};

const formatFilenameAsTitle = (filename) => {
  if (!filename) return null;
  return filename
    .replace(/\.(mermaid\.enc|mermaid|enc|pdf)$/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase()) || null;
};

const getMapTitleFromSnapshot = (snap) => {
  if (!snap) return null;
  const nodes = snap.diagnostic_subgraph?.nodes || [];
  // Prefer the source of a node that is on the active traversal path.
  // The full graph contains nodes from ALL mermaid files (sorted alphabetically),
  // so the first node in the array may be from a different file than the active workflow.
  // Prefer visited / traversed nodes: the full graph may contain every file
  // the product owns, so the first node in array order often names a
  // Salesforce case PDF rather than the active workflow.
  const visitedNids = new Set([
    ...(snap.traversal_path || []),
    ...(snap.visited_nodes || []),
  ]);
  if (visitedNids.size > 0) {
    const nodeMap = Object.fromEntries(nodes.map((n) => [n.nid, n]));
    for (const nid of visitedNids) {
      const n = nodeMap[nid];
      const src = n && (n.sources || [])[0];
      if (src) return formatFilenameAsTitle(src);
    }
  }
  // Fallback: first node with a source in the full graph
  for (const n of nodes) {
    const src = (n.sources || [])[0];
    if (src) return formatFilenameAsTitle(src);
  }
  return null;
};

const normalizeType = (type) => {
  if (!type) return "Info";
  if (type === "DiagnosticStep") return "DiagnosticSteps";
  return type;
};

/* ── Shape mapping ── */
const SHAPE_MAP = {
  Problem: "diamond", Decision: "diamond",
  PossibleCause: "hexagon",
  Product: "cylinder",
  Symptom: "parallelogram",
  Workflow: "subroutine",
  Resolution: "stadium",
  Remediation: "roundedRect",
  Workaround: "trapezoid",
  Info: "flag",
  DiagnosticSteps: "rect",
  DiagnosticStep: "rect",
};
const shapeOf = (type) => SHAPE_MAP[type] || "rect";

/* ── Compact node sizing ── */
// Must stay in sync with TraversalGraph constants so H is sized to fit exact line count.
const CHAR_W = 5.5;
const LINE_H = 13;
const NODE_PAD = 0.08;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const computeNodeSize = (shape, textLen) => {
  const n = textLen;

  // Step 1: compute W from text length (unchanged)
  let W, minH, maxH;
  switch (shape) {
    case "diamond":    W = clamp(100 + n * 1.5, 140, 240); minH = 100; maxH = 240; break;
    case "hexagon":    W = clamp(100 + n * 1.5, 140, 230); minH = 90;  maxH = 200; break;
    case "cylinder":   W = clamp(90  + n * 1.4, 120, 210); minH = 70;  maxH = 160; break;
    case "stadium":
    case "subroutine":
    case "parallelogram":
    case "trapezoid":  W = clamp(90  + n * 1.5, 130, 230); minH = 70;  maxH = 180; break;
    case "roundedRect":W = clamp(90  + n * 1.4, 120, 220); minH = 70;  maxH = 200; break;
    default:           W = clamp(90  + n * 1.4, 120, 220); minH = 70;  maxH = 220; break;
  }

  // Step 2: derive charsPerLine from W using the same formula TraversalGraph uses
  let cpl;
  if (shape === "diamond") {
    // TraversalGraph: textAreaW = (W/2)*(1-PAD)
    cpl = Math.max(Math.floor((W / 2) * (1 - NODE_PAD) / CHAR_W), 8);
  } else if (shape === "hexagon") {
    // textAreaH/2 / hh = (H*(1-PAD*2)/2) / (H/2) = 1-PAD*2 — H cancels, exact match to TraversalGraph
    const indent = Math.min(18, (W / 2) * 0.25);
    const approxCpl = ((W / 2) - indent * (1 - NODE_PAD * 2)) * 2 * (1 - NODE_PAD);
    cpl = Math.max(Math.floor(approxCpl / CHAR_W), 8);
  } else {
    // TraversalGraph: textAreaW = W*(1-PAD)
    cpl = Math.max(Math.floor(W * (1 - NODE_PAD) / CHAR_W), 8);
  }

  // Step 3: estimate lines needed (50% overhead for word-boundary wrapping gaps)
  const nLines = Math.ceil((n / cpl) * 1.5);

  // Step 4: compute H so textAreaH fits exactly nLines*LINE_H
  // diamond textAreaH = (H/2)*(1-PAD)  →  H = nLines*LINE_H*2/(1-PAD)
  // others  textAreaH = H*(1-PAD*1.5)  →  H = nLines*LINE_H/(1-PAD*1.5)
  const hNeeded = shape === "diamond"
    ? Math.ceil(nLines * LINE_H * 2 / (1 - NODE_PAD))
    : shape === "hexagon"
    ? Math.ceil(nLines * LINE_H / ((1 - NODE_PAD * 2) * (1 - NODE_PAD)))
    : Math.ceil(nLines * LINE_H / (1 - NODE_PAD * 1.5));

  const H = clamp(hNeeded, minH, maxH);
  return { W, H };
};

// TraversalGraph handles line-level truncation with "…" — no pre-truncation needed.
const truncForShape = (_shape, text) => text || "";

const buildFallbackGraph = (trail = []) => {
  const gNodes = [], gEdges = [], nodeMap = {};
  const seen = new Set();
  let idx = 0;
  trail.forEach((raw, index) => {
    const baseId = raw.nid || raw.id || `node-${index}`;
    const id = seen.has(baseId) ? `${baseId}-${index}` : baseId;
    seen.add(id); idx++;
    const type = normalizeType(raw.type);
    const shape = shapeOf(type);
    const name = raw.name || raw.label || raw.question || raw.text || id;
    const { W, H } = computeNodeSize(shape, name.length);
    const label = truncForShape(shape, name, W);
    const nd = {
      id, type, shape, name, label,
      description: raw.description || "",
      W, H,
      visited: true, turnVisited: raw.turn || idx,
      walkTurn: Number.isInteger(raw.turn) ? raw.turn : null,
      x: 80 + (idx - 1) * 160, y: 80,
    };
    gNodes.push(nd); nodeMap[id] = nd;
    if (gNodes.length > 1) {
      gEdges.push({ from: gNodes[gNodes.length - 2].id, to: id, rel_type: "NEXT", step: gNodes.length - 1, traversed: true });
    }
  });
  return { gNodes, gEdges, nodeMap, curNid: gNodes[gNodes.length - 1]?.id || null, eliminatedSet: new Set(), pathComplete: true, stepHops: gEdges.map(e => ({ step: e.step, from: e.from, to: e.to })) };
};

// Stable identity for the closed case so downstream memos do not re-run.
// Must mirror buildSnapshotGraph's return shape exactly — the component destructures
// these names and reads .length on them, so a mismatched key throws while closed.
const EMPTY_GRAPH = {
  gNodes: [],
  gEdges: [],
  nodeMap: {},
  curNid: null,
  eliminatedSet: new Set(),
  pathComplete: true,
  stepHops: [],
};

const buildSnapshotGraph = (snapshot = null, fallbackTrail = []) => {
  const hasSnapshot = snapshot && Object.keys(snapshot).length > 0;
  if (!hasSnapshot) return buildFallbackGraph(fallbackTrail);

  const subgraph = snapshot.diagnostic_subgraph || {};
  const subNodes = subgraph.nodes || [];
  const subEdges = subgraph.edges || [];
  if (!subNodes.length) {
    if (fallbackTrail.length) return buildFallbackGraph(fallbackTrail);
    // Snapshot exists but has no subgraph nodes and no trail to fall back to.
    // Inherit `path_complete` from the snapshot rather than asserting true —
    // the snapshot may carry a known gap even when its subgraph is empty.
    return { gNodes: [], gEdges: [], nodeMap: {}, curNid: snapshot.current_node_nid || null, eliminatedSet: new Set(snapshot.eliminated_causes || []), pathComplete: snapshot.path_complete !== false, stepHops: [] };
  }

  const visitedDetails = snapshot.visited_node_details || {};
  const curNid = snapshot.current_node_nid || null;
  const traversalPath = snapshot.traversal_path || snapshot.visited_nodes || [];
  const visitedSet = new Set([...Object.keys(visitedDetails), ...(snapshot.visited_nodes || []), ...traversalPath]);
  if (curNid) visitedSet.add(curNid);

  // The hops of the walk, one per consecutive pair of the trail, numbered
  // 1..(N-1) by TRAIL POSITION — step k is always "node k -> node k+1". The two
  // badge systems on the canvas are read together (blue = the node's ordinal in
  // the walk, red = the step on the edge into it), so step k MUST stay tied to
  // the trail index; compacting the numbers over drawable hops only made "Step
  // 10" land on the node badged 12. A repeated pair keeps the FIRST hop that
  // claimed it (an edge can only carry one red badge), but the numbers of the
  // later hops are untouched — they are still their own steps, they just have no
  // badge of their own to show.
  const traversalHops = [];
  const seenEdgeKeys = new Set();
  for (let i = 0; i < traversalPath.length - 1; i++) {
    const from = traversalPath[i], to = traversalPath[i + 1];
    if (!from || !to) continue;
    // `step` is the trail position, NOT a running counter over kept hops.
    traversalHops.push({ from, to, step: i + 1, badged: !seenEdgeKeys.has(`${from}->${to}`) });
    seenEdgeKeys.add(`${from}->${to}`);
  }
  // Only the badge-carrying hops may claim an edge row (same dedupe as before).
  const badgedHops = traversalHops.filter(h => h.badged);
  // Traversal hops are looked up below by the STORED edge orientation, which
  // may be the opposite of the direction actually walked — a backtrack walks
  // B→A while the runbook stores A→B. Register BOTH orientations (same trick as
  // bridgeEdgeLookup below). Reversed entries are written FIRST and forward
  // entries SECOND so a genuine forward hop always wins over a reversed match
  // on the same stored edge.
  const traversalEdgeMap = {};
  badgedHops.forEach(e => { traversalEdgeMap[`${e.to}->${e.from}`] = { ...e, reversed: true }; });
  badgedHops.forEach(e => { traversalEdgeMap[`${e.from}->${e.to}`] = { ...e, reversed: false }; });

  // Bridge-route edges: mark real graph edges that lie on a backtrack/return
  // route so the renderer can highlight them distinctly. Each consecutive pair
  // in a bridge `route` is checked in BOTH orientations because the stored
  // edge direction may be opposite to the walked direction.
  const bridgeEdgeLookup = {};
  (snapshot.path_bridges || []).forEach(bridge => {
    const route = bridge.route;
    if (!Array.isArray(route) || route.length < 2) return;
    for (let i = 0; i < route.length - 1; i++) {
      const walkFrom = route[i], walkTo = route[i + 1];
      // Natural: stored edge runs same direction as walk
      bridgeEdgeLookup[`${walkFrom}->${walkTo}`] = { reversed: false };
      // Reversed: stored edge runs opposite to walk
      bridgeEdgeLookup[`${walkTo}->${walkFrom}`] = { reversed: true };
    }
  });

  const nodeInfoMap = {};
  subNodes.forEach(n => { if (n.nid) nodeInfoMap[n.nid] = n; });
  const allNodeIds = subNodes.map(n => n.nid).filter(nid => nid && nodeInfoMap[nid]);
  const allNodeIdSet = new Set(allNodeIds);

  const drawnEdges = subEdges.filter(e => allNodeIdSet.has(e.from_nid) && allNodeIdSet.has(e.to_nid));

  const edgeRows = drawnEdges
    .map(e => {
      const storedKey = `${e.from_nid}->${e.to_nid}`;
      const bi = bridgeEdgeLookup[storedKey];
      const ti = traversalEdgeMap[storedKey];
      return {
        from: e.from_nid, to: e.to_nid, rel_type: e.rel_type, condition: e.condition,
        traversed: Boolean(ti),
        step: ti?.step,
        // true when the walk crossed this edge against its stored direction
        traversedReversed: ti?.reversed || false,
        bridge: !!bi,
        bridgeReversed: bi?.reversed || false,
      };
    });

  // NOTE: Synthetic "TRAVERSED" / "Step N" edges are NO LONGER fabricated
  // client-side. The server now path-fills the traversal along real graph
  // edges; any remaining gap is a genuine discontinuity and must stay visible.

  /* ── Dagre layout with Mermaid Explorer sizing ── */
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", nodesep: 40, ranksep: 55, marginx: 30, marginy: 30 });
  g.setDefaultEdgeLabel(() => ({}));

  const nodeShapes = {};
  allNodeIds.forEach(nid => {
    const info = nodeInfoMap[nid] || {};
    const type = normalizeType(info.type);
    const shape = shapeOf(type);
    const name = info.name || nid;
    const { W, H } = computeNodeSize(shape, name.length);
    nodeShapes[nid] = { type, shape, name, W, H, description: info.description || "" };
    g.setNode(nid, { width: W, height: H });
  });
  edgeRows.forEach(e => { g.setEdge(e.from, e.to, {}); });
  dagre.layout(g);

  const gNodes = [], nodeMap = {};
  allNodeIds.forEach(nid => {
    const pos = g.node(nid);
    if (!pos) return;
    const ns = nodeShapes[nid];
    const label = truncForShape(ns.shape, ns.name, ns.W);
    // `via` distinguishes nodes the model explicitly reasoned about ("visited")
    // from path-fill nodes the server inserted along real edges ("passed_through").
    // Legacy snapshots (no `via` field) default to "visited" so nothing regresses.
    const via = visitedDetails[nid]?.via || "visited";
    const nd = {
      id: nid, type: ns.type, shape: ns.shape,
      name: ns.name, label, description: ns.description,
      W: ns.W, H: ns.H,
      visited: visitedSet.has(nid),
      via: visitedSet.has(nid) ? via : undefined,
      turnVisited: traversalPath.includes(nid) ? traversalPath.indexOf(nid) + 1 : undefined,
      // The CONVERSATION turn the walk reached this node on (server-stamped). Distinct
      // from `turnVisited`, which is the node's POSITION in the walk (the canvas badge).
      // null on legacy snapshots, on nodes the walk never logged, and on visits logged
      // outside a turn — the trace panel groups those separately, it never invents one.
      walkTurn: Number.isInteger(visitedDetails[nid]?.turn) ? visitedDetails[nid].turn : null,
      x: pos.x, y: pos.y,
    };
    gNodes.push(nd); nodeMap[nid] = nd;
  });

  edgeRows.forEach(e => {
    const dagreEdge = g.edge(e.from, e.to);
    if (dagreEdge && dagreEdge.points) {
      e.points = dagreEdge.points;
    }
  });

  // `path_complete` — true when every consecutive pair in traversal_path is
  // joined by a real edge; false when at least one gap remains. Missing field
  // (legacy) is treated as true (no warning).
  const pathComplete = snapshot.path_complete !== false;
  // Every hop of the walk, in order, with the node it LANDED on. The trace
  // navigator walks THIS — not the edges that happen to carry a red badge: a hop
  // whose connection is not drawable (a bridged backtrack, or the pair left
  // behind when an unresolved id was dropped) has no edge to find, and looking
  // steps up by edge made the arrows dead-end on it. It still focuses its
  // destination node; it simply shows no badge.
  const stepHops = traversalHops.map(h => ({ step: h.step, from: h.from, to: h.to }));
  return { gNodes, gEdges: edgeRows, nodeMap, curNid, eliminatedSet: new Set(snapshot.eliminated_causes || []), pathComplete, stepHops };
};

export default function DiagnosticMapWindow({
  open, minimized, inline = false, standalone = false, hideTrace = false, onOpen, onMinimize, onClose,
  trail = [], snapshot = null, fullSnapshot = null, focusNodeId = null, focusKey = 0,
  maxTurn = null, title = "Diagnostic Map", isGraphLoading = false,
  pendingEdge = null,
}) {
  // For inline (mini) mode: focused neighbourhood from the cached full graph.
  // - top-most visited node (traversal_path[0]) → 3-layer ancestors up
  // - intermediate visited nodes → just the node, no expansion
  // - bottom-most visited node (current_node_nid) → 3-layer descendants down
  const displaySnapshot = useMemo(() => {
    if (!inline) return snapshot;

    const fullGraph = fullSnapshot?.diagnostic_subgraph || snapshot?.diagnostic_subgraph;
    if (!fullGraph?.nodes?.length) return snapshot;

    const traversalPath = snapshot?.traversal_path || [];
    const visitedNids = new Set([
      ...(snapshot?.visited_nodes || []),
      ...traversalPath,
    ]);
    const curNid = snapshot?.current_node_nid
      || traversalPath[traversalPath.length - 1]
      || null;
    const rootNid = traversalPath[0] || null;

    if (!visitedNids.size && !curNid) return snapshot;

    const fullNodes = fullGraph.nodes;
    const fullEdges = fullGraph.edges || [];
    const fullNodeMap = new Map(fullNodes.map(n => [n.nid, n]));

    const childrenMap = {};
    const parentMap = {};
    fullEdges.forEach(e => {
      const from = e.from_nid ?? e.from;
      const to   = e.to_nid   ?? e.to;
      if (!from || !to) return;
      if (!childrenMap[from]) childrenMap[from] = [];
      childrenMap[from].push(to);
      if (!parentMap[to]) parentMap[to] = [];
      parentMap[to].push(from);
    });

    const keepNids = new Set(visitedNids);
    if (curNid) keepNids.add(curNid);

    // 3-layer ancestors from the top-most visited node only
    if (rootNid) {
      let frontier = [rootNid];
      for (let d = 0; d < 3; d++) {
        const next = [];
        frontier.forEach(nid => (parentMap[nid] || []).forEach(pid => {
          if (!keepNids.has(pid) && fullNodeMap.has(pid)) { keepNids.add(pid); next.push(pid); }
        }));
        frontier = next;
        if (!frontier.length) break;
      }
    }

    // 3-layer descendants from the current (bottom-most) node only
    if (curNid) {
      let frontier = [curNid];
      for (let d = 0; d < 3; d++) {
        const next = [];
        frontier.forEach(nid => (childrenMap[nid] || []).forEach(cid => {
          if (!keepNids.has(cid) && fullNodeMap.has(cid)) { keepNids.add(cid); next.push(cid); }
        }));
        frontier = next;
        if (!frontier.length) break;
      }
    }

    const nodes = [...keepNids].map(nid => fullNodeMap.get(nid)).filter(Boolean);
    const edges = fullEdges.filter(e => {
      const from = e.from_nid ?? e.from;
      const to   = e.to_nid   ?? e.to;
      return keepNids.has(from) && keepNids.has(to);
    });

    return {
      ...snapshot,
      diagnostic_subgraph: { nodes, edges },
    };
  }, [inline, snapshot, fullSnapshot]);

  // Gated on `open`. Both instances of this window stay mounted, and the early
  // `if (!open) return null` sits below the hooks — so without this gate every
  // tse:done laid out the full graph (196 nodes today) twice with both windows
  // closed, on the same thread as the chat. EMPTY_GRAPH is module-level so the
  // closed case keeps a stable identity and does not retrigger downstream memos.
  // buildSnapshotGraph runs a synchronous dagre layout over every node and
  // edge. Inside useMemo that executed DURING the render that opens the window,
  // so the click froze the tab until the whole graph was laid out and only then
  // painted — "hangs and opens late". Defer it past first paint: the window
  // shows immediately (its Loading graph… state keys off gNodes.length === 0),
  // then the layout runs and swaps in.
  const [graph, setGraph] = useState(EMPTY_GRAPH);
  useEffect(() => {
    if (!open) {
      setGraph(EMPTY_GRAPH);
      return undefined;
    }
    let cancelled = false;
    const schedule = window.requestIdleCallback || window.requestAnimationFrame;
    const cancel = window.cancelIdleCallback || window.cancelAnimationFrame;
    const handle = schedule.call(window, () => {
      if (!cancelled) setGraph(buildSnapshotGraph(displaySnapshot || {}, trail));
    });
    return () => {
      cancelled = true;
      try { cancel.call(window, handle); } catch (_) { /* best effort */ }
    };
  }, [open, displaySnapshot, trail]);
  const { gNodes, gEdges, nodeMap, curNid, eliminatedSet, pathComplete, stepHops } = graph;
  const displayTitle = useMemo(() =>
    getMapTitleFromSnapshot(snapshot) || getMapTitleFromSnapshot(fullSnapshot) || title,
  [snapshot, fullSnapshot, title]);

  // Every runbook this graph was built from. The server now scopes the map to the
  // runbooks the session actually loaded rather than every runbook the product owns,
  // so naming them is what tells the engineer whether they are looking at one
  // flowchart or several linked ones merged onto a single canvas.
  const selectedRunbooks = useMemo(() => {
    const list = snapshot?.selected_runbooks || fullSnapshot?.selected_runbooks || [];
    return list.map((name) => formatFilenameAsTitle(name)).filter(Boolean);
  }, [snapshot, fullSnapshot]);

  // A snapshot that carries a `diagnostic_subgraph` — even an empty one — has
  // been answered; the loading overlay must not park on "Loading graph…" forever
  // just because the subgraph happened to be empty.
  //
  // The third clause covers the DEFERRED LAYOUT gap. `graph` starts as
  // EMPTY_GRAPH and is only filled by the requestIdleCallback above, so between
  // "fetch resolved" and "dagre finished" the fetch flag is already false while
  // gNodes is still empty. Without this the loader vanishes and the canvas sits
  // blank for as long as the layout takes — seconds on the 3105-node ZIA graph —
  // which reads as "no map found" rather than "still working". Gated on the
  // SOURCE subgraph actually having nodes so a genuinely empty result still
  // resolves to the empty state instead of spinning forever.
  const sourceNodeCount = snapshot?.diagnostic_subgraph?.nodes?.length || 0;
  const awaitingLayout = sourceNodeCount > 0 && gNodes.length === 0;
  const awaitingGraph =
    isGraphLoading || (!!snapshot && !snapshot.diagnostic_subgraph) || awaitingLayout;

  const canvasW = gNodes.length ? Math.max(700, ...gNodes.map(n => n.x + n.W / 2 + 60)) : 700;
  const canvasH = gNodes.length ? Math.max(300, ...gNodes.map(n => n.y + n.H / 2 + 60)) : 300;
  const typesUsed = [...new Set(gNodes.map(n => n.type))];
  // In standalone (source-map) mode the snapshot is isolated — no current
  // node should highlight, even if buildSnapshotGraph somehow derived one.
  const activeNids = standalone ? new Set() : new Set([curNid].filter(Boolean));
  const isFaded = (nd) => maxTurn != null && nd.turnVisited != null && nd.turnVisited > maxTurn;

  const graphRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [, forceUpdate] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  // Trace Path panel: per-row description disclosure (collapsed by default) and
  // the row whose id was just copied, for brief feedback.
  const [expandedTraceRows, setExpandedTraceRows] = useState(() => new Set());
  // Per-turn section state, stored as OVERRIDES of the default (only the most recent
  // turn is open) so a growing walk keeps opening on its newest turn.
  const [traceTurnOverrides, setTraceTurnOverrides] = useState({});
  const [copiedTraceKey, setCopiedTraceKey] = useState(null);
  const copyTimerRef = useRef(null);
  const [hiddenTypes, setHiddenTypes] = useState(new Set());
  const [loaderTimedOut, setLoaderTimedOut] = useState(false);
  const loaderTimerRef = useRef(null);

  // Show loader when map opens with no data; auto-dismiss after 10s if API is stuck
  useEffect(() => {
    const shouldLoad = open && gNodes.length === 0 && awaitingGraph;
    if (shouldLoad) {
      setLoaderTimedOut(false);
      if (!loaderTimerRef.current) {
        loaderTimerRef.current = setTimeout(() => {
          setLoaderTimedOut(true);
          loaderTimerRef.current = null;
        }, 10000);
      }
    } else {
      setLoaderTimedOut(false);
      if (loaderTimerRef.current) { clearTimeout(loaderTimerRef.current); loaderTimerRef.current = null; }
    }
    return () => {
      if (loaderTimerRef.current) { clearTimeout(loaderTimerRef.current); loaderTimerRef.current = null; }
    };
  }, [open, gNodes.length, awaitingGraph]);

  // Copy feedback timer must not outlive the window.
  useEffect(() => () => { if (copyTimerRef.current) clearTimeout(copyTimerRef.current); }, []);

  const handleCopyTraceId = useCallback(async (rowKey, nid) => {
    const ok = await copyTextToClipboard(nid);
    if (!ok) return;
    setCopiedTraceKey(rowKey);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => { setCopiedTraceKey(null); copyTimerRef.current = null; }, 1400);
  }, []);

  const toggleTraceRow = useCallback((rowKey) => {
    setExpandedTraceRows(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
      return next;
    });
  }, []);

  // Turn sections hold only the groups the user EXPLICITLY toggled; everything else
  // falls back to the default (last group open). Keeping it as overrides rather than a
  // seeded set means the newest turn stays open as the walk grows, while a group the
  // user opened or closed by hand keeps whatever they chose — and toggling one group
  // never touches another, nor the per-row description toggles.
  const toggleTraceTurn = useCallback((groupKey, isOpen) => {
    setTraceTurnOverrides(prev => ({ ...prev, [groupKey]: !isOpen }));
  }, []);

  // The window shell (drag, resize, bubble, z ladder) is the shared
  // FloatingPanel. Inline = draggable small frame with a bubble when minimised;
  // full = maximised, with no bubble (minimised full map renders nothing).
  const panelState = !open
    ? PANEL_STATES.HIDDEN
    : minimized
      ? (inline ? PANEL_STATES.BUBBLE : PANEL_STATES.HIDDEN)
      : (inline ? PANEL_STATES.SMALL : PANEL_STATES.MAXIMISED);
  const shellProps = {
    panelState,
    title,
    draggable: inline,
    resizable: inline,
    className: inline ? MAP_FRAME_CLASS_INLINE : MAP_FRAME_CLASS_FULL,
    frameProps: MAP_FRAME_PROPS,
    resizeHandleClassNames: MAP_RESIZE_CLASSES,
    renderBubble: () => (
      <button type="button" onClick={onOpen}
        className="absolute right-3 top-[82px] z-[70] flex items-center justify-center w-9 h-9 rounded-full bg-[#06b6d4] text-white shadow-lg hover:bg-[#0891b2] transition-colors"
        title="Open mini diagnostic map">
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="12" cy="5" r="3" /><circle cx="5" cy="19" r="3" /><circle cx="19" cy="19" r="3" />
          <path d="M12 8v3M7.5 17.5L10.5 11M16.5 17.5L13.5 11" />
        </svg>
      </button>
    ),
  };

  // Same root element type on every return, so the panel keeps its dragged
  // position and size across minimise and restore.
  if (panelState === PANEL_STATES.HIDDEN || panelState === PANEL_STATES.BUBBLE) {
    return <FloatingPanel {...shellProps} />;
  }

  const typeCounts = {};
  gNodes.forEach(nd => { typeCounts[nd.type] = (typeCounts[nd.type] || 0) + 1; });
  const sortedTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]);

  const filteredNodes = hiddenTypes.size > 0 ? gNodes.filter(nd => !hiddenTypes.has(nd.type)) : gNodes;
  const filteredEdges = hiddenTypes.size > 0 ? gEdges.filter(e => {
    const fn = nodeMap[e.from], tn = nodeMap[e.to];
    return fn && tn && !hiddenTypes.has(fn.type) && !hiddenTypes.has(tn.type);
  }) : gEdges;
  const filteredNodeMap = {};
  filteredNodes.forEach(nd => { filteredNodeMap[nd.id] = nd; });

  // In inline mode, center on explicit focusNodeId or fall back to curNid
  const inlineFocusId = inline ? (focusNodeId || curNid) : null;

  // Inline focus mode: show center node + 3 ancestor hops (visited history) + 3 descendant hops (future possible paths)
  // Unvisited descendants appear dimmed automatically via focusMode in TraversalGraph
  let displayNodes = filteredNodes;
  let displayEdges = filteredEdges;
  let displayNodeMap = filteredNodeMap;
  // `nodeMap[inlineFocusId]` is required: a focus id that is not IN the graph
  // (a node from another runbook, a logged id that never folded onto a mermaid
  // node) produced an empty neighbourhood — "No graph data yet" on the canvas
  // under a header counting nodes. Falling back to the whole neighbourhood is
  // the honest answer when the focus cannot be placed.
  if (inline && inlineFocusId && nodeMap[inlineFocusId]) {
    const parentsMap = {}, childrenMap = {};
    // Use full gEdges so hidden types don't break the hop chain
    gEdges.forEach(e => {
      if (!childrenMap[e.from]) childrenMap[e.from] = [];
      childrenMap[e.from].push(e.to);
      if (!parentsMap[e.to]) parentsMap[e.to] = [];
      parentsMap[e.to].push(e.from);
    });
    const included = new Set([inlineFocusId]);
    let frontier = [inlineFocusId];
    // 3 hops up — visited ancestors
    for (let d = 0; d < 3; d++) {
      const next = [];
      frontier.forEach(nid => (parentsMap[nid] || []).forEach(pid => { if (!included.has(pid)) { included.add(pid); next.push(pid); } }));
      frontier = next;
      if (!frontier.length) break;
    }
    // 3 hops down — future possible paths (unvisited, will render dimmed)
    frontier = [inlineFocusId];
    for (let d = 0; d < 3; d++) {
      const next = [];
      frontier.forEach(nid => (childrenMap[nid] || []).forEach(cid => { if (!included.has(cid)) { included.add(cid); next.push(cid); } }));
      frontier = next;
      if (!frontier.length) break;
    }
    displayNodes = gNodes.filter(n => included.has(n.id));
    displayEdges = gEdges.filter(e => included.has(e.from) && included.has(e.to));
    displayNodeMap = {};
    displayNodes.forEach(n => { displayNodeMap[n.id] = n; });
  }

  const searchResults = searchQuery.trim()
    ? gNodes.filter(nd => {
        const q = searchQuery.toLowerCase();
        return (nd.id && nd.id.toLowerCase().includes(q)) ||
               (nd.name && nd.name.toLowerCase().includes(q)) ||
               (nd.type && nd.type.toLowerCase().includes(q));
      })
    : [];

  /* ── Trace Path panel: the walk, in order ───────────────────────────────────
     Sourced from `stepHops` — the SAME hop list the canvas step navigator counts
     — so the panel can never disagree with the badges. Node 1 of the walk is the
     first hop's origin; node k+1 is hop k's destination. A walk with a single
     node produces no hops at all, so that one case falls back to the snapshot's
     own traversal list (which is what stepHops was built from anyway).
     Row numbers use `turnVisited`, which is literally what the blue canvas badge
     renders, so a revisited node reads the same number in both places. */
  const tracePath = (() => {
    const raw = [];
    if (stepHops && stepHops.length) {
      const hops = [...stepHops].sort((a, b) => a.step - b.step);
      raw.push({ id: hops[0].from, step: null });
      hops.forEach(h => raw.push({ id: h.to, step: h.step }));
    } else if (!standalone) {
      const walk = displaySnapshot?.traversal_path || displaySnapshot?.visited_nodes || [];
      walk.forEach((id, i) => raw.push({ id, step: i > 0 ? i : null }));
    }
    const rows = raw.filter(r => r.id);
    return rows.map((r, i) => {
      const nd = nodeMap[r.id];
      return {
        key: `${i}-${r.id}`,
        id: r.id,
        nd,
        ord: nd?.turnVisited ?? i + 1,
        // Legacy snapshots carry no `via`; those nodes are plain visited.
        via: nd?.via || "visited",
        step: r.step,
        // Conversation turn (server-stamped) or null when unknown — see `walkTurn`.
        turn: Number.isInteger(nd?.walkTurn) ? nd.walkTurn : null,
        isLast: i === rows.length - 1,
      };
    });
  })();
  /* Turn grouping. Rows are grouped in CONSECUTIVE runs, so the panel always reads in
     walk order and no row is ever moved or dropped: a run of rows with no known turn
     becomes its own neutral group in place instead of being relocated or hidden.
     A snapshot where NOTHING carries a turn (older backend, replayed session) yields no
     groups at all and the panel falls back to the flat list. */
  const traceHasTurns = tracePath.some(r => r.turn != null);
  const traceGroups = (() => {
    if (!traceHasTurns) return [];
    const out = [];
    tracePath.forEach(row => {
      const prev = out[out.length - 1];
      if (prev && prev.turn === row.turn) prev.rows.push(row);
      else out.push({ key: row.turn == null ? `u${row.key}` : `t${row.turn}`, turn: row.turn, rows: [row] });
    });
    return out;
  })();
  // Traceable map only: never in the standalone source-map viewer (no walk),
  // never in the inline Step Map (which hides the trace bar too), and never
  // when the walk is empty.
  const traceable = !inline && !standalone && tracePath.length > 0;

  const focusTraceRow = (row) => {
    // Reuse the canvas's own navigation: goToStep syncs the step counter, opens
    // the node inspector and pans — it already tolerates a target that is not on
    // the current canvas (hidden by a type filter). The first node of the walk
    // is the origin of step 1, not the landing of any step, so it uses the same
    // pan+select pair the Search panel uses.
    if (row.step != null) graphRef.current?.goToStep?.(row.step);
    else { graphRef.current?.panToNode?.(row.id); graphRef.current?.selectNode?.(row.id); }
  };

  /* One Trace Path row. Themed off the app's own palette (arta surface/border/text,
     cyan reserved for the CURRENT node and for interactive affordances); node type is
     carried by the same legend colour as the canvas, but as a small dot next to a type
     label — the Search panel's own idiom — instead of a filled colour chip per row. */
  const renderTraceRow = (row) => {
    const s = GNODE_STYLES[row.nd?.type] || GNODE_STYLES._default;
    const isPassed = row.via === "passed_through";
    const isOpen = expandedTraceRows.has(row.key);
    const isCurrent = row.id === curNid;
    const isCopied = copiedTraceKey === row.key;
    return (
      <div key={row.key} className="flex gap-2 items-stretch">
        {/* Rail: the walk ordinal — literally the canvas badge number — then a
            downward connector standing in for the "→". */}
        <div className="flex flex-col items-center w-5 shrink-0">
          <span
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold tabular-nums shrink-0 border ${
              isCurrent ? "bg-[#06b6d4] border-[#06b6d4] text-white" : "bg-white border-[#e2e5eb] text-gray-500"}`}
          >
            {row.ord}
          </span>
          {!row.isLast && (
            <>
              <span className="flex-1 w-px bg-[#e2e5eb] my-1" style={{ minHeight: 10 }} />
              <svg width="10" height="10" fill="none" stroke="#cbd5e1" strokeWidth="2.5" viewBox="0 0 24 24" className="shrink-0 mb-1">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </>
          )}
        </div>
        <div className="min-w-0 flex-1 pb-2">
          <div
            role="button"
            tabIndex={0}
            onClick={() => focusTraceRow(row)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); focusTraceRow(row); } }}
            title="Focus this node on the map"
            className={`rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${
              isCurrent
                ? "border-[#06b6d4] bg-[#ecfeff]"
                : isPassed
                  ? "border-[#86efac] border-dashed bg-[#f9fafb] hover:bg-white"
                  : "border-[#e2e5eb] bg-white hover:bg-[#f9fafb]"}`}
          >
            <div className="flex items-start gap-2 min-w-0">
              <div className="w-2.5 h-2.5 rounded-full shrink-0 mt-1 ring-2 ring-white" style={{ background: s.bg }} />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-medium text-gray-400 truncate">{row.nd?.type || "Unknown type"}</p>
                <p className="text-[12px] font-bold text-[#253439] leading-snug break-words">{row.nd?.name || row.id}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 mt-1.5">
              <div className="flex items-center gap-2 bg-[#f1f5f9] rounded-md px-2.5 py-1 min-w-0 flex-1">
                <span className="text-[9px] text-gray-400 shrink-0">ID</span>
                <span className="text-[10px] font-mono text-[#253439] break-all">{row.id}</span>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleCopyTraceId(row.key, row.id); }}
                title={isCopied ? "Copied" : "Copy node ID"}
                className={`w-6 h-6 flex items-center justify-center rounded-lg shrink-0 transition ${isCopied ? "text-[#0891b2] bg-cyan-50" : "text-gray-400 hover:text-[#06b6d4] hover:bg-cyan-50"}`}
              >
                {isCopied ? (
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
                ) : (
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                )}
              </button>
            </div>
            {isPassed && (
              /* Same reading as the canvas legend's dashed swatch, at panel weight. */
              <span className="inline-flex items-center gap-1 mt-1.5 text-[9px] font-medium text-gray-500">
                <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: "#bbf7d0", border: "1.5px dashed #86efac" }} />
                passed through
              </span>
            )}
            {row.nd?.description && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleTraceRow(row.key); }}
                  className="mt-1.5 flex items-center gap-1 text-[9px] font-semibold text-gray-400 hover:text-[#06b6d4] transition-colors"
                >
                  <svg width="9" height="9" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                  {isOpen ? "Hide description" : "Description"}
                </button>
                {isOpen && (
                  <p className="text-[10px] text-gray-500 leading-relaxed mt-1 break-words">{row.nd.description}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Header of the map; FloatingPanel hands it the drag handle props (inline only).
  const renderMapHeader = ({ dragHandleProps }) => (
      <div
        className={`flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 gap-3 ${inline ? "cursor-grab active:cursor-grabbing" : ""}`}
        {...(dragHandleProps || {})}
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {/* Left: icon + title + subtitle */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#06b6d4] text-white shadow-sm">
            <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="3" /><circle cx="5" cy="19" r="3" /><circle cx="19" cy="19" r="3" />
              <path d="M12 8v3M7.5 17.5L10.5 11M16.5 17.5L13.5 11" />
            </svg>
          </div>
          <div>
            <p className="text-[14px] font-bold text-[#253439] leading-tight">{title}</p>
            {/* displayNodes, not gNodes: in inline focus mode the canvas draws a
                neighbourhood, and counting the full set let the header claim
                nodes that were not on screen. Header and canvas must agree. */}
            {/* Standalone source maps are isolated — no traversal state — so
                "0 visited" would be misleading. Show only the node count. */}
            {standalone ? (
              <p className="text-[11px] text-gray-400">{displayNodes.length} nodes</p>
            ) : (
            <p className="text-[11px] text-gray-400">
              {displayNodes.filter(n => n.visited && n.via !== "passed_through").length} visited
              {displayNodes.filter(n => n.via === "passed_through").length > 0 && (
                <span className="text-gray-300">{` · ${displayNodes.filter(n => n.via === "passed_through").length} pass-through`}</span>
              )}
              {` · ${displayNodes.length} shown`}
              {!pathComplete && <span className="text-amber-500 ml-1" title="At least one gap in the walked path — no real edge connects consecutive nodes">(path has a gap)</span>}
            </p>
            )}
          </div>
        </div>

        {/* Center: legend */}
        {!inline && typesUsed.length > 0 && (
          <div className="flex items-center gap-4 flex-wrap overflow-hidden flex-1 px-4">
            {typesUsed.map(type => {
              const s = GNODE_STYLES[type] || GNODE_STYLES._default;
              const shapeFn = LEGEND_SHAPE[type] || LEGEND_SHAPE._default;
              return (
                <div key={type} className="flex items-center gap-1.5 shrink-0">
                  <svg width="16" height="14" viewBox="0 0 14 12" className="shrink-0">
                    {shapeFn({ fill: s.bg, stroke: s.border, strokeWidth: 1.5 })}
                  </svg>
                  <span className="text-[11px] font-medium text-[#475569]">{type}</span>
                </div>
              );
            })}
            {/* Visit-state legend: solid green = model reasoned here, dashed lighter = path-fill.
                Traversal-only — meaningless in the standalone source-map viewer (no walk). */}
            {!hideTrace && (
              <>
                <div className="w-px h-4 bg-gray-200 mx-1 shrink-0" />
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ background: "#22c55e", border: "1.5px solid #16a34a" }} />
                  <span className="text-[10px] text-[#475569]">Visited</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ background: "#bbf7d0", border: "1.5px dashed #86efac" }} />
                  <span className="text-[10px] text-[#475569]">Passed through</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <svg width="14" height="6" viewBox="0 0 14 6" className="shrink-0">
                    <line x1="0" y1="3" x2="14" y2="3" stroke="#f87171" strokeWidth="2" strokeDasharray="3 2" />
                  </svg>
                  <span className="text-[10px] text-[#475569]">Return path</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Right: action buttons */}
        <div className="flex items-center gap-1 shrink-0">
          {!inline && (
            <button onClick={() => { setShowSearch(!showSearch); if (!showSearch) { setShowFilter(false); setShowTrace(false); } }}
              className={`rounded-lg p-2 transition ${showSearch ? "bg-[#06b6d4] text-white" : "text-gray-400 hover:bg-cyan-50 hover:text-[#06b6d4]"}`} title="Search Nodes">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            </button>
          )}
          {!inline && (
            <button onClick={() => { setShowFilter(!showFilter); if (!showFilter) { setShowSearch(false); setShowTrace(false); } }}
              className={`rounded-lg p-2 transition ${showFilter ? "bg-[#06b6d4] text-white" : "text-gray-400 hover:bg-cyan-50 hover:text-[#06b6d4]"}`} title="Filter by Type">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
            </button>
          )}
          {/* Trace Path — traceable maps only (never standalone, never inline) */}
          {traceable && (
            <button onClick={() => { setShowTrace(!showTrace); if (!showTrace) { setShowSearch(false); setShowFilter(false); } }}
              className={`rounded-lg p-2 transition ${showTrace ? "bg-[#06b6d4] text-white" : "text-gray-400 hover:bg-cyan-50 hover:text-[#06b6d4]"}`} title="Trace Path">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="6" cy="5" r="2.5" /><circle cx="18" cy="19" r="2.5" />
                <path d="M6 7.5v4a3 3 0 003 3h6a3 3 0 013 3v-1" />
              </svg>
            </button>
          )}
          {/* A standalone tab has no owner to minimize into and nothing to
              close back to — the browser tab IS the window. */}
          {!inline && !standalone && <div className="w-px h-5 bg-gray-200 mx-0.5" />}
          {!standalone && (
            <button type="button" onClick={onMinimize} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition" title="Minimize">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14" /></svg>
            </button>
          )}
          {!standalone && (
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-500 transition" title="Close">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          )}
        </div>
      </div>
  );

  return (
    <FloatingPanel {...shellProps} renderHeader={renderMapHeader}>
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 flex flex-col min-w-0 min-h-0 relative">
          {/* Graph-loading overlay — shown until nodes arrive or 10s timeout */}
          {/* The overlay stays up for as long as the graph is genuinely still being
              resolved. It used to disappear at the 10s mark, which uncovered the
              canvas's own empty state — a message about triage identifying candidate
              paths, under a header reading "0 nodes". Nothing had failed and nothing
              had finished; the request was still in flight. Removing a spinner is not
              an answer, so past the timeout the overlay says the true thing instead. */}
          {gNodes.length === 0 && awaitingGraph && (
            <div className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 bg-white/80 backdrop-blur-[2px]">
              {!loaderTimedOut && (
                <svg className="animate-spin text-cyan-500" width="28" height="28" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
              <span className="text-[12px] font-medium text-[#0e7490]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {loaderTimedOut ? "Still loading the graph…" : "Loading graph…"}
              </span>
              {loaderTimedOut && (
                <span className="text-[11px] text-gray-400 max-w-[260px] text-center" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  This is taking longer than usual. The graph has not failed — nothing
                  has come back yet.
                </span>
              )}
            </div>
          )}
          {!inline && (selectedRunbooks.length > 0 || displayTitle) && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 pointer-events-none max-w-[calc(100%-48px)]">
              {/* One pill per runbook on the canvas. Falls back to the derived title
                  when the server did not report a selection (older snapshots, and the
                  cache-served path before a runbook has been claimed). */}
              <div className="flex items-center justify-center gap-2 flex-wrap" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {(selectedRunbooks.length > 0 ? selectedRunbooks : [displayTitle]).map((name) => (
                  <div key={name} className="flex items-center gap-2 px-4 py-1.5 bg-white/95 backdrop-blur-sm border border-cyan-300 rounded-full shadow-md overflow-hidden max-w-[300px]" title={name}>
                    <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0" />
                    <span className="text-[12px] font-semibold text-[#0e7490] truncate">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <TraversalGraph
            ref={graphRef}
            gNodes={displayNodes}
            gEdges={displayEdges}
            nodeMap={displayNodeMap}
            curNid={standalone ? null : curNid}
            activeNids={activeNids}
            focusNodeId={inline ? inlineFocusId : focusNodeId}
            focusKey={focusKey}
            eliminatedSet={eliminatedSet}
            fadedNids={new Set(gNodes.filter(isFaded).map(n => n.id))}
            canvasW={canvasW}
            canvasH={canvasH}
            typesUsed={typesUsed}
            emptyMessage={standalone ? {
              headline: "No graph for this document",
              detail: "This document has no diagnostic flowchart to draw.",
            } : null}
            GNODE_STYLES={GNODE_STYLES}
            R={50}
            hideToolbar
            inline={inline}
            hideTrace={hideTrace}
            mapTitle={displayTitle}
            pendingEdge={pendingEdge}
            stepHops={stepHops}
          />
        </div>

        {/* Search Panel */}
        {showSearch && (
          <div className="w-72 border-l border-gray-200 bg-white flex flex-col shrink-0 overflow-hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
              <span className="text-sm font-bold text-[#253439]">Search Nodes</span>
              <button onClick={() => setShowSearch(false)} className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-4 pb-3">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text" value={searchQuery} autoFocus
                  onChange={(e) => { setSearchQuery(e.target.value); graphRef.current?.search(e.target.value); forceUpdate(n => n + 1); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); graphRef.current?.searchNav(e.shiftKey ? -1 : 1); forceUpdate(n => n + 1); }
                    if (e.key === "Escape") { setSearchQuery(""); graphRef.current?.search(""); }
                  }}
                  placeholder="Search..."
                  className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-[#06b6d4] focus:ring-1 focus:ring-cyan-200 transition-all placeholder-gray-400 text-[#253439]"
                />
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(""); graphRef.current?.search(""); forceUpdate(n => n + 1); }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition"
                  >
                    <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-2 pb-3">
              {searchQuery.trim() && searchResults.length === 0 && (
                <p className="text-center text-xs text-gray-400 py-6">No results found</p>
              )}
              {searchResults.map(nd => {
                const s = GNODE_STYLES[nd.type] || GNODE_STYLES._default;
                return (
                  <button key={nd.id}
                    onClick={() => { graphRef.current?.panToNode?.(nd.id); graphRef.current?.selectNode?.(nd.id); }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-start gap-2.5 group"
                  >
                    <div className="w-2.5 h-2.5 rounded-full shrink-0 mt-1 ring-2 ring-white" style={{ background: s.bg }} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold text-[#253439] truncate group-hover:text-[#06b6d4] transition-colors">{nd.id}</span>
                        <span className="text-[9px] text-gray-400 shrink-0">{nd.type}</span>
                      </div>
                      <p className="text-[10px] text-gray-500 truncate leading-snug mt-0.5">{nd.name}</p>
                    </div>
                  </button>
                );
              })}
              {!searchQuery.trim() && (
                <p className="text-center text-xs text-gray-400 py-6">Type to search nodes by ID, name, or type</p>
              )}
              {searchQuery.trim() && searchResults.length > 0 && (
                <p className="text-center text-[10px] text-gray-400 pt-2">{searchResults.length} results</p>
              )}
            </div>
          </div>
        )}

        {/* Filter by Type Panel */}
        {showFilter && (
          <div className="w-72 border-l border-gray-200 bg-white flex flex-col shrink-0 overflow-hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <div className="flex items-center justify-between px-4 pt-3 pb-3">
              <span className="text-sm font-bold text-[#253439]">Filter by Type</span>
              <button onClick={() => setShowFilter(false)} className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-0.5">
              {sortedTypes.map(([type, count]) => {
                const s = GNODE_STYLES[type] || GNODE_STYLES._default;
                const isHidden = hiddenTypes.has(type);
                return (
                  <div key={type} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ background: s.bg, opacity: isHidden ? 0.3 : 1 }} />
                      <span className={`text-xs font-medium truncate ${isHidden ? "text-gray-400" : "text-[#253439]"}`}>{type}</span>
                    </div>
                    <div className="flex items-center gap-2.5 shrink-0">
                      <span className="text-[10px] text-gray-400 font-mono tabular-nums">{count}</span>
                      <button
                        onClick={() => {
                          setHiddenTypes(prev => {
                            const next = new Set(prev);
                            if (next.has(type)) next.delete(type); else next.add(type);
                            return next;
                          });
                        }}
                        className={`relative w-8 h-[18px] rounded-full transition-colors ${isHidden ? "bg-gray-300" : "bg-[#06b6d4]"}`}
                      >
                        <div className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform ${isHidden ? "left-[2px]" : "left-[14px]"}`} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Trace Path Panel — the walk, top to bottom */}
        {showTrace && traceable && (
          <div className="w-80 border-l border-gray-200 bg-white flex flex-col shrink-0 overflow-hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <div className="flex items-center justify-between px-4 pt-3 pb-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-bold text-[#253439]">Trace Path</span>
                <span className="text-[10px] text-gray-400 font-mono tabular-nums shrink-0">{tracePath.length}</span>
              </div>
              <button onClick={() => setShowTrace(false)} className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-4">
              {/* Grouped by conversation turn when the snapshot carries turns, flat
                  otherwise (older backend / replayed session). */}
              {traceGroups.length > 0
                ? traceGroups.map((grp, gi) => {
                    const isTurnOpen = grp.key in traceTurnOverrides
                      ? traceTurnOverrides[grp.key]
                      : gi === traceGroups.length - 1;   // default: newest turn open
                    /* A turn that walked NO graph node (a digression answered off the
                       map) has no rows, so it gets no section — which used to read as a
                       missing turn. Name it instead: a hairline between the two sections
                       that says which turn numbers were skipped and why. Only between two
                       KNOWN, non-adjacent turns — never before the first section, never
                       after the last, and never around an "Unspecified turn" run (a null
                       turn says nothing about the numbering). Not clickable, not counted:
                       it holds no rows and never touches step numbering. */
                    const prevTurn = gi > 0 ? traceGroups[gi - 1].turn : null;
                    const gapFrom = Number.isInteger(prevTurn) ? prevTurn + 1 : null;
                    const gapTo = Number.isInteger(grp.turn) ? grp.turn - 1 : null;
                    const gapLabel = (gapFrom != null && gapTo != null && gapTo >= gapFrom)
                      ? (gapTo === gapFrom
                          ? `turn ${gapFrom} skipped — no diagnostic steps`
                          : `turns ${gapFrom}-${gapTo} skipped — no diagnostic steps`)
                      : null;
                    return (
                      <React.Fragment key={grp.key}>
                      {gapLabel && (
                        <div className="flex items-center gap-2 px-2 py-1.5 select-none">
                          <span className="flex-1 h-px bg-[#e2e5eb]" />
                          <span className="text-[10px] text-[#6e7687] whitespace-nowrap">{gapLabel}</span>
                          <span className="flex-1 h-px bg-[#e2e5eb]" />
                        </div>
                      )}
                      <div className="mb-1">
                        <button
                          type="button"
                          onClick={() => toggleTraceTurn(grp.key, isTurnOpen)}
                          aria-expanded={isTurnOpen}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#f9fafb] transition-colors"
                        >
                          <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"
                            className="shrink-0 text-gray-400"
                            style={{ transform: isTurnOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                          <span className="text-[11px] font-bold text-[#253439] truncate">
                            {grp.turn == null ? "Unspecified turn" : `Turn ${grp.turn}`}
                          </span>
                          <span className="flex-1 h-px bg-[#e2e5eb]" />
                          <span className="text-[10px] text-gray-400 font-mono tabular-nums shrink-0">{grp.rows.length}</span>
                        </button>
                        {isTurnOpen && (
                          <div className="pt-1">{grp.rows.map(renderTraceRow)}</div>
                        )}
                      </div>
                      </React.Fragment>
                    );
                  })
                : tracePath.map(renderTraceRow)}
            </div>
          </div>
        )}
      </div>
      {/* Resize handles (inline only) are rendered by FloatingPanel with MAP_RESIZE_CLASSES. */}
    </FloatingPanel>
  );
}
